import PropTypes from 'prop-types';
import _ from 'underscore';
import type { Dispatcher } from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';
import BaseStore from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';

import ThirdPartyAccounts from 'bundles/third-party-auth/models/ThirdPartyAccounts';

type AccountType = string; // ie apple, facebook

class AccountLinkStore extends BaseStore {
  static storeName = 'AccountLinkStore' as const;

  static handlers = {
    CURRENT_ACCOUNT_SET: 'handleCurrentAccount',
    LOADING: 'handleLoading',
    ERROR: 'handleError',
    ACCOUNT_LINK_SUCCESS: 'handleLinkSuccess',
    ACCOUNT_DELINK_SUCCESS: 'handleDelinkSuccess',
    ACCOUNT_LINK_GET_ACCOUNT_SUCCESS: 'handleGetAccountSuccess',
    DISMISS_PASSWORD_DIALOG: 'handleDismissPasswordDialog',
  };

  static SHAPE = {
    currentAccountType: PropTypes.string.isRequired,
    loading: PropTypes.bool.isRequired,
    linked: PropTypes.shape({
      facebook: PropTypes.bool,
      apple: PropTypes.bool,
      google: PropTypes.bool,
      saml: PropTypes.bool,
    }).isRequired,
    requirePassword: PropTypes.bool.isRequired,
    errorCode: PropTypes.string,
    successCode: PropTypes.string,
  };

  declare linked: {
    [key in AccountType]?: boolean;
  };

  declare currentAccountType: string | null;

  declare loading: boolean;

  declare requirePassword: boolean;

  declare errorCode?: string | null;

  declare successCode?: null | 'link' | 'delink';

  declare linkedAccounts?: ThirdPartyAccounts;

  constructor(dispatcher: Dispatcher) {
    super(dispatcher);
    this.linked = {};
    this.currentAccountType = '';
    this.loading = false;
    this.requirePassword = false;
  }

  handleCurrentAccount({ currentAccount }: { currentAccount: string }) {
    if (this.currentAccountType !== currentAccount) {
      this.resetMessages();
      this.currentAccountType = currentAccount;
      this.emitChange();
    }
  }

  handleLoading() {
    this.loading = true;
    this.emitChange();
  }

  handleError({ errorCode }: { errorCode?: string }) {
    this.errorCode = errorCode || 'unknownCode';
    this.requirePassword = this.errorCode === 'noPasswordSupplied';
    this.successCode = null;
    this.emitLoadedChange();
  }

  handleLinkSuccess({ accountType }: { accountType: AccountType }) {
    this.linked[accountType] = true;
    this.successCode = 'link';
    this.errorCode = null;
    this.requirePassword = false;
    this.emitLoadedChange();
  }

  handleDelinkSuccess({ accountType }: { accountType: AccountType }) {
    this.linked[accountType] = false;
    this.errorCode = null;
    this.requirePassword = false;
    this.successCode = 'delink';
    this.emitLoadedChange();
  }

  handleDismissPasswordDialog() {
    this.requirePassword = false;
    this.errorCode = null;
    this.emitChange();
  }

  handleGetAccountSuccess({ linkedAccounts }: { linkedAccounts: { elements: { id: string }[] } }) {
    this.linkedAccounts = new ThirdPartyAccounts(linkedAccounts);
    this.linked = this.linkedAccounts.getLinkedAccountMap();
    this.emitLoadedChange();
  }

  emitLoadedChange() {
    this.loading = false;
    this.emitChange();
  }

  dehydrate() {
    // @ts-expect-error FIXME
    return _(this).pick(_(AccountLinkStore.SHAPE).keys());
  }

  rehydrate(state: Record<string, unknown>) {
    Object.assign(this, _(state).pick(_(AccountLinkStore.SHAPE).keys()));
  }

  resetMessages() {
    this.currentAccountType = null;
    this.loading = false;
    this.requirePassword = false;
    this.errorCode = null;
    this.successCode = null;
    this.emitChange();
  }

  getLink() {
    return this.linked;
  }
}

export default AccountLinkStore;
